/**
 * BillingSystem.js  — AMOS 고지서 관리 (리팩토링)
 *
 * 변경 사항:
 *   - useResidentApi()       : 거주자 CRUD 5개 함수 공통 훅으로 추출
 *                              (saveResident / saveNewResident / deactivate /
 *                               activate / delete 의 중복 fetch 패턴 통합)
 *   - apiPost() / apiFetch() : fetch 래퍼로 에러 처리 일원화
 *   - ResidentForm           : 거주자 폼 UI를 독립 컴포넌트로 분리
 *   - 나머지 로직·API 시그니처 변경 없음
 */

import React, { useState, useEffect, useCallback, useRef } from "react";
import { getLabels } from "../fieldLabels";

// ══════════════════════════════════════════════════════════════
// 유틸
// ══════════════════════════════════════════════════════════════

const formatNumber = (v) => {
  if (v === undefined || v === null || v === "") return "0";
  const s = String(v).replace(/[^0-9-]/g, "");
  return s ? Number(s).toLocaleString() : "0";
};

const formatMoney = (val, unit) => {
  const num = Number(String(val).replace(/[^0-9-]/g, "")) || 0;
  if (unit === "억") return (num / 100_000_000).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 }) + "억";
  if (unit === "만") return Math.floor(num / 10_000).toLocaleString() + "만";
  return formatNumber(val);
};

// ══════════════════════════════════════════════════════════════
// 스타일
// ══════════════════════════════════════════════════════════════

const COLORS = { elec: "#f59e0b", gas: "#ef4444", water: "#3b82f6" };

const styles = {
  container:   { backgroundColor: "#f0f2f5", minHeight: "100vh", padding: "15px", fontFamily: "sans-serif" },
  btnPrimary:  (bg) => ({ flex: 1, padding: "12px 5px", backgroundColor: bg, color: "#fff", border: "none", borderRadius: "12px", fontWeight: "bold", fontSize: "13px", cursor: "pointer" }),
  btnSmall:    (bg) => ({ padding: "3px 10px", backgroundColor: bg, color: "#fff", border: "none", borderRadius: "6px", fontSize: "10px", fontWeight: "bold", cursor: "pointer" }),
  btnFull:     (bg) => ({ width: "100%", padding: "12px", backgroundColor: bg, color: "#fff", border: "none", borderRadius: "10px", fontWeight: "bold", display: "flex", justifyContent: "space-between", fontSize: "14px", cursor: "pointer" }),
  btnToggle:   { width: "100%", marginTop: "12px", padding: "6px", fontSize: "11px", border: "none", backgroundColor: "rgba(0,0,0,0.05)", borderRadius: "8px", color: "#888", cursor: "pointer" },
  card:        (bg, border) => ({ backgroundColor: bg, marginBottom: "12px", padding: "16px", borderRadius: "15px", borderLeft: "8px solid " + border, boxShadow: "0 4px 6px rgba(0,0,0,0.07)" }),
  detailBox:   { marginTop: "10px", padding: "12px", backgroundColor: "#2d3436", borderRadius: "10px", color: "#fff" },
  meterBox:    { backgroundColor: "#fff", borderRadius: "15px", padding: "15px", border: "1px solid #ddd" },
  overlay:     { position: "fixed", inset: 0, backgroundColor: "rgba(0,0,0,0.45)", zIndex: 1000, display: "flex", alignItems: "flex-end", justifyContent: "center" },
  sheet:       { backgroundColor: "#fff", borderRadius: "20px 20px 0 0", padding: "24px 20px 32px", width: "100%", maxWidth: "500px", boxSizing: "border-box", maxHeight: "85vh", overflowY: "auto" },
  label:       { fontSize: "12px", color: "#64748b", fontWeight: "600", marginBottom: "4px", display: "block" },
  input:       { width: "100%", padding: "10px", borderRadius: "10px", border: "1px solid #e2e8f0", fontSize: "14px", boxSizing: "border-box", outline: "none", marginBottom: "12px" },
};

// ══════════════════════════════════════════════════════════════
// 거주자 API 훅 (중복 제거 핵심)
// ══════════════════════════════════════════════════════════════

/**
 * useResidentApi
 * - 5가지 CRUD 함수(저장/신규/퇴거/활성/삭제)의 공통 패턴을 단일 훅으로 통합
 * - 각 함수는 완료 후 자동으로 rooms/billing 데이터를 갱신
 */
function useResidentApi({ API_BASE, token, onRoomsUpdated, onBillingUpdated, setMsg, setSaving }) {
  const headers = useCallback(
    () => ({ "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) }),
    [token]
  );

  /**
   * _runRoomAction: 공통 패턴
   *   1. setSaving(true)
   *   2. fetch
   *   3. 성공 → setMsg('✅ ...'), 데이터 갱신
   *      실패 → setMsg('❌ ...')
   *   4. setSaving(false)
   */
  const _runRoomAction = useCallback(async ({ url, method, body, successMsg, failMsg, onSuccess }) => {
    setSaving(true); setMsg("");
    try {
      const res = await fetch(url, { method, headers: headers(), ...(body ? { body: JSON.stringify(body) } : {}) });
      if (res.ok) {
        setMsg(successMsg);
        await Promise.all([onRoomsUpdated?.(), onBillingUpdated?.()]);
        onSuccess?.();
      } else {
        const err = await res.json().catch(() => ({}));
        setMsg("❌ " + (err.detail || failMsg));
      }
    } catch { setMsg("❌ 서버 오류"); }
    finally  { setSaving(false); }
  }, [headers, onRoomsUpdated, onBillingUpdated, setMsg, setSaving]);

  const save = useCallback((roomId, form) =>
    _runRoomAction({
      url: `${API_BASE}/api/rooms/${roomId}`, method: "PATCH", body: form,
      successMsg: "✅ 저장 완료", failMsg: "저장 실패",
    }), [_runRoomAction, API_BASE]);

  const add = useCallback((form, onSuccess) =>
    _runRoomAction({
      url: `${API_BASE}/api/rooms`, method: "POST", body: form,
      successMsg: "✅ 등록 완료", failMsg: "등록 실패", onSuccess,
    }), [_runRoomAction, API_BASE]);

  const deactivate = useCallback((roomId) =>
    _runRoomAction({
      url: `${API_BASE}/api/rooms/${roomId}`, method: "DELETE",
      successMsg: "✅ 퇴거 처리 완료", failMsg: "퇴거 처리 실패",
    }), [_runRoomAction, API_BASE]);

  const activate = useCallback((roomId) =>
    _runRoomAction({
      url: `${API_BASE}/api/rooms/${roomId}`, method: "PATCH", body: { is_active: true },
      successMsg: "✅ 활성 처리 완료", failMsg: "활성 처리 실패",
    }), [_runRoomAction, API_BASE]);

  const hardDelete = useCallback((roomId) =>
    _runRoomAction({
      url: `${API_BASE}/api/rooms/${roomId}/hard`, method: "DELETE",
      successMsg: "✅ 삭제 완료", failMsg: "삭제 실패",
    }), [_runRoomAction, API_BASE]);

  return { save, add, deactivate, activate, hardDelete };
}

// ══════════════════════════════════════════════════════════════
// 거주자 폼 (분리된 컴포넌트)
// ══════════════════════════════════════════════════════════════

function ResidentForm({ form, setForm, mode, L }) {
  const update = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  return (
    <>
      {mode === "add" && (
        <>
          <label style={styles.label}>{L.room_no} *</label>
          <input value={form.room_no || ""} onChange={(e) => setForm((f) => ({ ...f, room_no: e.target.value.replace(/[^0-9]/g, "") }))}
            placeholder="예: 201" inputMode="numeric" style={styles.input} />
        </>
      )}

      <label style={styles.label}>이름 *</label>
      <input value={form.user_name || ""} onChange={update("user_name")} placeholder={L.user_name_ph} style={styles.input} />

      <label style={styles.label}>전화번호 *</label>
      <input value={form.phone || ""} onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value.replace(/[^0-9]/g, "") }))}
        placeholder="01012345678" inputMode="tel" style={styles.input} />
      <label style={styles.label}>차량번호</label>
      <input value={form.car_number || ""} onChange={update("car_number")} placeholder="예: 12가3456" style={styles.input} />

      <label style={styles.label}>이메일</label>
      <input value={form.email || ""} onChange={update("email")} placeholder="example@email.com" style={styles.input} />

      {/* 금액 3개 */}
      <div style={{ display: "flex", gap: "8px" }}>
        {[["deposit", "보증금"], ["monthly_rent", "월세"], ["mgmt_fee", "관리비"]].map(([key, label]) => (
          <div key={key} style={{ flex: 1 }}>
            <label style={styles.label}>{label}</label>
            <input type="number" value={form[key] ?? ""} onChange={update(key)} placeholder="0" style={styles.input} />
          </div>
        ))}
      </div>

      {/* 날짜 2개 */}
      <div style={{ display: "flex", gap: "8px" }}>
        {[["move_in_date", "입주일"], ["expiry_date", "만기일"]].map(([key, label]) => (
          <div key={key} style={{ flex: 1 }}>
            <label style={styles.label}>{label}</label>
            <input type="date" value={form[key] || ""} onChange={update(key)} style={styles.input} />
          </div>
        ))}
      </div>

      <label style={styles.label}>메모</label>
      <textarea value={form.memo || ""} onChange={update("memo")} placeholder="특이사항 등" rows={2}
        style={{ ...styles.input, resize: "none", fontFamily: "inherit" }} />
    </>
  );
}

// ══════════════════════════════════════════════════════════════
// 서브 카드 컴포넌트
// ══════════════════════════════════════════════════════════════

const OWNER_GROUPS = [
  { label: "아드리아", rooms: ["201","202","203","204","205","206","207","208","209","210"], color: "#2563eb", bg: "#eff6ff" },
  { label: "이우석",   rooms: null, color: "#059669", bg: "#f0fdf4" },
  { label: "이준영",   rooms: ["306","402","403","404","502","503","505"],                   color: "#0891b2", bg: "#ecfeff" },
  { label: "이여영",   rooms: ["302","303","405","406","407","501","504"],                   color: "#7c3aed", bg: "#f5f3ff" },
];

const SummaryCard = ({ item, regularCount, regularItems, ownerFilter, setOwnerFilter, setIsTotalListOpen }) => {
  const [open, setOpen] = React.useState(false);
  const allRooms = OWNER_GROUPS.flatMap(g => g.rooms || []);
  return (
    <>
      <div style={{ ...styles.card("#f8fff9", "#2ecc71"), cursor: 'pointer' }} onClick={() => setOpen(o => !o)}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "4px" }}>
          <div style={{ flex: 1.2 }}></div>
          <div style={{ fontSize: "10px", color: "#9ca3af", flex: 2, display: "flex", justifyContent: "space-around" }}>
            <span>보증금</span><span>월세</span><span>관리비</span>
          </div>
          <div style={{ fontSize: "10px", color: "#9ca3af", flex: 1, textAlign: "right" }}>총금액</div>
        </div>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div style={{ fontSize: "12px", fontWeight: "bold", color: "#27ae60", flex: 1.2 }}>📊 총 {regularCount}세대</div>
          <div style={{ fontSize: "12px", color: "#444", flex: 2, display: "flex", justifyContent: "space-around" }}>
            <span>{formatMoney(item.deposit, "억")}</span>
            <span>{formatMoney(item.total_rent || item.monthly_rent, "만")}</span>
            <span>{formatMoney(item.total_mgmt || "0", "만")}</span>
          </div>
          <div style={{ fontSize: "12px", fontWeight: "bold", color: "#27ae60", flex: 1, textAlign: "right" }}>{formatMoney(item.총금액, "만")}</div>
        </div>
      </div>
      {open && OWNER_GROUPS.map((g) => {
        const items = regularItems.filter((x) => {
          const r = String(x.호수 || "").replace("호", "");
          return g.rooms ? g.rooms.includes(r) : !allRooms.includes(r);
        });
        const totalAmt     = items.reduce((s, x) => s + (Number(String(x.총금액 || "0").replace(/,/g, "")) || 0), 0);
        const totalRent    = items.reduce((s, x) => s + (Number(String(x.월세   || "0").replace(/[^0-9]/g, "")) || 0), 0);
        const totalMgmt    = items.reduce((s, x) => s + (Number(String(x.관리비 || "0").replace(/[^0-9]/g, "")) || 0), 0);
        const totalDeposit = items.reduce((s, x) => s + (Number(String(x.deposit || x.보증금 || "0").replace(/[^0-9]/g, "")) || 0), 0);
        const isSelected   = ownerFilter === g.label;
        return (
          <div key={g.label} style={{ ...styles.card(g.bg, g.color), cursor: 'pointer',
                                      outline: isSelected ? `2px solid ${g.color}` : 'none' }}
            onClick={() => {
              const next = isSelected ? null : g.label;
              setOwnerFilter(next);
              setIsTotalListOpen(true);
            }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "4px" }}>
              <div style={{ fontSize: "12px", fontWeight: "bold", color: g.color, flex: 1.2 }}>{g.label}</div>
              <div style={{ fontSize: "10px", color: "#9ca3af", flex: 2, display: "flex", justifyContent: "space-around" }}>
                <span>보증금</span><span>월세</span><span>관리비</span>
              </div>
              <div style={{ fontSize: "10px", color: "#9ca3af", flex: 1, textAlign: "right" }}>총금액</div>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div style={{ fontSize: "11px", color: "#6b7280", flex: 1.2 }}>{items.length}세대</div>
              <div style={{ fontSize: "12px", color: "#444", flex: 2, display: "flex", justifyContent: "space-around" }}>
                <span>{Math.round(totalDeposit/100000000*100)/100}억</span>
                <span>{Math.round(totalRent/10000).toLocaleString()}만</span>
                <span>{Math.round(totalMgmt/10000).toLocaleString()}만</span>
              </div>
              <div style={{ fontSize: "12px", fontWeight: "bold", color: g.color, flex: 1, textAlign: "right" }}>{Math.round(totalAmt/10000).toLocaleString()}만</div>
            </div>
          </div>
        );
      })}
    </>
  );
};

const RegularCard = ({ item, openId, setOpenId, onSend, onClearEdit }) => {
  const bg       = item._adjusted ? "#fffbeb" : (item.is_unpaid ? "#FFF5F5" : "#fff");
  const cleanHosu = String(item.호수 || "").replace("호", "");
  const allRooms  = OWNER_GROUPS.flatMap(g => g.rooms || []);
  const ownerGrp  = OWNER_GROUPS.find(g => g.rooms ? g.rooms.includes(cleanHosu) : !allRooms.includes(cleanHosu));
  const border   = item._adjusted ? "#f59e0b" : (item.is_unpaid ? "#ff7675" : (ownerGrp?.color || "#007bff"));
  const cleanName = String(item.owner || "").replace("님", "");
  const phone     = item.phone?.length > 5 ? item.phone.replace(/(\d{3})(\d{4})(\d{4})/, "$1-$2-$3") : "";

  return (
    <div style={styles.card(bg, border)}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "stretch" }}>
        <div style={{ flex: 1.2, display: "flex", flexDirection: "column", justifyContent: "center", gap: "2px" }}>
          <div style={{ fontSize: "14px", fontWeight: "bold", display: "flex", gap: "6px", alignItems: "center" }}>
            <span style={{ color: "#27ae60" }}>{cleanHosu}</span>
            <span style={{ color: "#000" }}>{cleanName}</span>
            {item._adjusted && <span style={{ fontSize: "9px", color: "#f59e0b", fontWeight: "bold" }}>✏️수정됨</span>}
          </div>
          <div style={{ fontSize: "10px", color: "#e11d48", fontWeight: "bold" }}>{phone}</div>
          {item.car_number && (
            <div style={{ fontSize: "10px", color: "#64748b", fontWeight: "600" }}>{item.car_number}</div>
          )}
        </div>
        <div style={{ flex: 1.5, textAlign: "center", display: "flex", flexDirection: "column", justifyContent: "center", gap: "3px" }}>
          <div style={{ fontSize: "12px", color: item.is_urgent_expiry ? "#d63031" : "#0984e3", fontWeight: "bold" }}>
            ~{item.expiry_date || "날짜없음"}
            {item.move_in_date && item.expiry_date && (() => {
              const yrs = Math.round((new Date(item.expiry_date) - new Date(item.move_in_date)) / (1000 * 60 * 60 * 24 * 365));
              return yrs > 0 ? <span style={{ fontSize: "10px", color: item.is_urgent_expiry ? "#d63031" : "#0984e3", marginLeft: "4px" }}>({yrs})</span> : null;
            })()}
          </div>
          <div style={{ fontSize: "13px", color: "#444", fontWeight: "500" }}>
            {formatMoney(item.deposit, "만")} / {formatMoney(item.monthly_rent, "만")}
          </div>
        </div>
        <div style={{ flex: 1.1, display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "flex-end", gap: "4px" }}>
          <div style={{ fontSize: "13px", fontWeight: "bold" }}>{formatNumber(item.총금액)}원</div>
          <div style={{ display: "flex", gap: "4px" }}>
            {item._adjusted && <button onClick={() => onClearEdit(item.호수)} style={styles.btnSmall("#9ca3af")}>↩</button>}
            <button onClick={() => onSend(item)} style={styles.btnSmall("#5c67f2")}>발송</button>
          </div>
        </div>
      </div>
      <button onClick={() => setOpenId(openId === item.id ? null : item.id)} style={styles.btnToggle}>
        {openId === item.id ? "▲ 닫기" : "▼ 상세내역"}
      </button>
      {openId === item.id && (
        <div style={styles.detailBox}>
          <pre style={{ margin: 0, whiteSpace: "pre-wrap", fontSize: "11px", lineHeight: "1.5", fontFamily: "'Malgun Gothic', '맑은 고딕', 'Apple SD Gothic Neo', monospace" }}>
            {item.미리보기 || item.상세내역}
          </pre>
        </div>
      )}
    </div>
  );
};

// ══════════════════════════════════════════════════════════════
// 메인 컴포넌트
// ══════════════════════════════════════════════════════════════

function BillingSystem({ API_BASE, tenantId, token, dailyDate, setDailyDate, readings, initialAction, onActionDone, template }) {
  const L        = getLabels(template);
  const CACHE_KEY = `billing_cache_${tenantId}`;

  const [list,       setList]       = useState(() => {
    try { const c = localStorage.getItem(CACHE_KEY); return c ? JSON.parse(c) : []; } catch { return []; }
  });
  const ADJUST_KEY = `adjust_map_${tenantId}`;
  const [adjustMap, setAdjustMap] = useState(() => {
    try { const c = localStorage.getItem(ADJUST_KEY); return c ? JSON.parse(c) : {}; } catch { return {}; }
  });

  // 수정 바텀시트
  const [editOpen,        setEditOpen]        = useState(false);
  const [editHosu,        setEditHosu]        = useState("");
  const [editMoveIn,      setEditMoveIn]      = useState("");
  const [editMoveOut,     setEditMoveOut]     = useState("");
  const [editMoveInElec,  setEditMoveInElec]  = useState(null);
  const [editMoveOutElec, setEditMoveOutElec] = useState(null);
  const [editExtras,      setEditExtras]      = useState([]);
  const [dateChanged,     setDateChanged]     = useState(false); // 날짜를 직접 변경했을 때만 true

  // 거주자 관리 바텀시트
  const [residentOpen, setResidentOpen] = useState(false);
  const [rooms,        setRooms]        = useState([]);
  const [resMode,      setResMode]      = useState("select");
  const [selRoomId,    setSelRoomId]    = useState("");
  const [resForm,      setResForm]      = useState({});
  const [resSaving,    setResSaving]    = useState(false);
  const [resMsg,       setResMsg]       = useState("");

  // 기타
  const [openId,            setOpenId]            = useState(null);
  const [isTotalListOpen,   setIsTotalListOpen]   = useState(false);
  const [ownerFilter,       setOwnerFilter]       = useState(null);
  const [isSending,         setIsSending]         = useState(false);
  const [isMeterSending,    setIsMeterSending]    = useState(false);
  const [selectedMetrics,   setSelectedMetrics]   = useState(["elec", "gas", "water"]);
  const [targetMonthStr,    setTargetMonthStr]    = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
  });

  const authHeaders = useCallback(() => ({
    "Content-Type": "application/json",
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  }), [token]);



  const summaryItems  = list.filter((x) => x.isSummary || x.호수 === "합계");
  const regularItems  = list.filter((x) => !x.isSummary && x.호수 !== "합계");

  // ── API 공통 ────────────────────────────────────────────────

  const fetchBillingData = useCallback(async (skipAuth = false) => {
    if (!skipAuth && !window.confirm("데이터를 새로고침합니다.")) return;
    try {
      const res  = await fetch(`${API_BASE}/api/billing-status?t=${Date.now()}`, { headers: authHeaders() });
      const data = await res.json();
      if (Array.isArray(data)) {
        setList(data);
        try { localStorage.setItem(CACHE_KEY, JSON.stringify(data)); } catch {}
      }
    } catch (e) { console.error("데이터 로드 실패:", e); }
  }, [API_BASE, authHeaders, CACHE_KEY]);

  const fetchRooms = useCallback(async () => {
    try {
      const res  = await fetch(`${API_BASE}/api/rooms`, { headers: authHeaders() });
      const data = await res.json();
      setRooms(Array.isArray(data) ? data : []);
    } catch (e) { console.error("거주자 목록 로드 실패:", e); }
  }, [API_BASE, authHeaders]);

  useEffect(() => { fetchBillingData(true); fetchRooms(); }, []);

  // ── 거주자 API 훅 ────────────────────────────────────────────

  const resApi = useResidentApi({
    API_BASE, token,
    onRoomsUpdated:   fetchRooms,
    onBillingUpdated: () => fetchBillingData(true),
    setMsg:           setResMsg,
    setSaving:        setResSaving,
  });

  // ── initialAction 처리 ──────────────────────────────────────

  const actionHandled = useRef(false);
  useEffect(() => {
    if (!initialAction || actionHandled.current) return;
    actionHandled.current = true;
    if (initialAction === "resident") {
      setResMode("select"); setSelRoomId(""); setResForm({}); setResMsg("");
      setResidentOpen(true); fetchRooms();
    } else if (initialAction === "edit") {
      setEditHosu(""); setEditMoveIn(""); setEditMoveOut("");
      setEditMoveInElec(null); setEditMoveOutElec(null); setEditExtras([]);
      setDateChanged(false);
      setEditOpen(true);
    }
    onActionDone?.();
  }, [initialAction]);

  // ── 거주자 관련 핸들러 ──────────────────────────────────────

  const onSelectRoom = (roomId) => {
    setSelRoomId(roomId);
    if (!roomId) { setResForm({}); setResMode("select"); return; }
    const room = rooms.find((r) => String(r.id) === String(roomId));
    if (!room) return;
    setResForm({
      user_name:    room.user_name    || "",
      phone:        room.phone        || "",
      car_number:   room.car_number   || "",
      email:        room.email        || "",
      deposit:      room.deposit      ?? 0,
      monthly_rent: room.monthly_rent ?? 0,
      mgmt_fee:     room.mgmt_fee     ?? 0,
      move_in_date: room.move_in_date ? room.move_in_date.slice(0, 10) : "",
      expiry_date:  room.expiry_date  ? room.expiry_date.slice(0, 10)  : "",
      memo:         room.memo         || "",
    });
    setResMode("edit");
  };

  const INT_FIELDS  = ['deposit', 'monthly_rent', 'mgmt_fee'];
  const DATE_FIELDS = ['move_in_date', 'expiry_date'];
  const toIntOrNull  = (v) => (v === '' || v == null) ? null : parseInt(v, 10) || 0;
  const toDateOrNull = (v) => (v === '' || v == null) ? null : v;
  const normalizeResVal = (key, v) => {
    if (INT_FIELDS.includes(key))  return toIntOrNull(v);
    if (DATE_FIELDS.includes(key)) return toDateOrNull(v);
    return v === '' ? null : v;
  };

  const saveResident = () => {
    const room = rooms.find((r) => String(r.id) === String(selRoomId));
    if (!room) return;
    const changed = {};
    Object.keys(resForm).forEach(key => {
      const newVal  = normalizeResVal(key, resForm[key]);
      const origVal = normalizeResVal(key, room[key] ?? '');
      if (String(newVal) !== String(origVal)) changed[key] = newVal;
    });
    if (Object.keys(changed).length === 0) { setResMsg('변경된 내용이 없습니다.'); return; }
    resApi.save(selRoomId, changed);
  };
  const saveNewResident = () => {
    if (!resForm.room_no || !resForm.user_name || !resForm.phone) {
      setResMsg("❌ " + L.required_msg); return;
    }
    resApi.add(resForm, () => { setResForm({}); setResMode("select"); });
  };

  const deactivateResident = () => {
    const room = rooms.find((r) => String(r.id) === String(selRoomId));
    if (!window.confirm(`${room?.room_no}${L.room_suffix} ${room?.user_name}님을 ${L.delete_confirm} 처리할까요?\n(데이터는 보존됩니다)`)) return;
    resApi.deactivate(selRoomId).then(() => { setResMode("select"); setSelRoomId(""); setResForm({}); });
  };

  const activateResident = () => {
    const room = rooms.find((r) => String(r.id) === String(selRoomId));
    if (!window.confirm(`${room?.room_no}${L.room_suffix} ${room?.user_name}님을 ${L.activate_confirm} 처리할까요?`)) return;
    resApi.activate(selRoomId);
  };

  const deleteResident = () => {
    const room = rooms.find((r) => String(r.id) === String(selRoomId));
    if (!window.confirm(`${room?.room_no}${L.room_suffix} ${room?.user_name}님 데이터를 완전히 삭제할까요?\n⚠️ 복구할 수 없습니다.`)) return;
    resApi.hardDelete(selRoomId).then(() => { setResMode("select"); setSelRoomId(""); setResForm({}); });
  };

  // ── 관리비 수정 ─────────────────────────────────────────────

  const onSelectHosu = (hosu) => {
    setEditHosu(hosu);
    const adj  = adjustMap[hosu] || {};
    const item = regularItems.find((x) => x.호수 === hosu) || {};
    setEditMoveIn(adj.moveIn   || item.cycle_start || "");
    setEditMoveOut(adj.moveOut || item.cycle_end   || "");
    setEditMoveInElec(adj.moveInElec   ?? null);
    setEditMoveOutElec(adj.moveOutElec ?? null);
    setEditExtras(adj.extras ? [...adj.extras] : []);
    setDateChanged(!!(adj.moveIn || adj.moveOut)); // 실제로 저장된 날짜가 있을 때만 true
  };

  const fetchElecOnDate = async (date, hosu, setter) => {
    if (!date || !hosu) return;
    try {
      const res  = await fetch(`${API_BASE}/api/meter-reading?room_no=${hosu.replace("호", "")}&date=${date}&tenant_id=${tenantId}`);
      const data = await res.json();
      setter(data.found ? data.elec_val : null);
    } catch { setter(null); }
  };

  const addExtra    = () => setEditExtras((p) => [...p, { name: "", amount: "" }]);
  const removeExtra = (i) => setEditExtras((p) => p.filter((_, idx) => idx !== i));
  const updateExtra = (i, f, v) => setEditExtras((p) => p.map((e, idx) => idx === i ? { ...e, [f]: v } : e));

  const saveEdit = async () => {
    const item = regularItems.find((x) => x.호수 === editHosu);
    if (!item) return;

    const origElec = Number(String(item.전기료 || "0").replace(/[^0-9-]/g, "")) || 0;
    // 이미 조정된 항목이면 저장된 원본값 사용, 아니면 rooms에서 조회
    const prevAdj  = adjustMap[editHosu];
    const latestRoomOrig = rooms.find(r => r.room_no === String(item.호수 || "").replace("호", ""));
    const origMgmt = prevAdj?.orig_mgmt ?? latestRoomOrig?.mgmt_fee ?? (Number(String(item.관리비 || "0").replace(/[^0-9-]/g, "")) || 0);
    const origRent = prevAdj?.orig_rent ?? latestRoomOrig?.monthly_rent ?? (Number(String(item.월세 || "0").replace(/[^0-9-]/g, "")) || 0);

    let elecFee = origElec, mgmtFee = origMgmt, rentFee = origRent;

    const hasProrate = dateChanged && !!(editMoveIn || editMoveOut);
    if (hasProrate) {
      const prevMatch = String(item.검침정보 || "").match(/전월([\d.]+)/);
      const currMatch = String(item.검침정보 || "").match(/당월([\d.]+)/);
      const prevElec  = prevMatch ? parseFloat(prevMatch[1]) : 0;
      const currElec  = currMatch ? parseFloat(currMatch[1]) : 0;

      try {
        const res  = await fetch(`${API_BASE}/api/calc-prorate`, {
          method: "POST", headers: authHeaders(),
          body: JSON.stringify({
            move_in_date:  editMoveIn  || null,
            move_out_date: editMoveOut || null,
            cycle_start:   item.cycle_start,
            cycle_end:     item.cycle_end,
            billing_month: item.billing_month,
            elec_prev:     prevElec,
            elec_curr:     currElec,
            move_in_elec:  editMoveInElec  ?? null,
            move_out_elec: editMoveOutElec ?? null,
            orig_mgmt:     origMgmt,
            orig_rent:     origRent,
          }),
        });
        const data = await res.json();
        elecFee = data.elec_fee;
        mgmtFee = data.mgmt_fee;
        rentFee = data.rent_fee;
      } catch (e) {
        alert("일할계산 오류: " + e.message);
        return;
      }
    }

    const newDuration = (() => {
      if (!hasProrate) return item.사용기간 || "";
      const start    = editMoveIn  ? new Date(editMoveIn)  : new Date(item.cycle_start);
      const end      = editMoveOut ? new Date(editMoveOut) : new Date(item.cycle_end);
      const usedDays = Math.round((end - start) / 86_400_000) + 1;
      return `기간:${editMoveIn || item.cycle_start || ""}~${editMoveOut || item.cycle_end || ""}(${usedDays}일)`;
    })();

    const extrasTotal = editExtras.reduce((s, e) => s + (Number(String(e.amount).replace(/[^0-9-]/g, "")) || 0), 0);

    const extras = editExtras.filter((e) => e.name && e.amount);

    // 한글 글자 수 카운트
    const hangulLen = (s) => [...s].filter(ch => {
      const c = ch.codePointAt(0);
      return c >= 0xAC00 && c <= 0xD7A3;
    }).length;

    const maxLabelHangul = Math.max(...extras.map(e => hangulLen(e.name)));
    const maxAmountLen   = Math.max(...extras.map(e => formatNumber(e.amount).length));

    const extrasDetail = extras
      .map((e) => {
        // 콜론 뒤에 두 가지 보정을 순서대로 삽입:
        // ① \u2003 (em space = 한글 1글자 폭) × 라벨 한글 부족분 → 라벨 길이 차이 보정
        // ② \u2007 (figure space = 숫자 1자리 폭) × 금액 자릿수 부족분 → 금액 자릿수 차이 보정
        const emPad     = ' '.repeat((maxLabelHangul - hangulLen(e.name)) * 2);
        const figurePad = ' '.repeat(Math.floor((maxAmountLen - formatNumber(e.amount).length) / 2));
        return `■ ${e.name} : ${emPad}${figurePad}${formatNumber(e.amount)}원`;
      })
      .join("\n");

    const newTotal = elecFee + mgmtFee + rentFee + extrasTotal;

    let preview = item.미리보기 || "";
    if (newDuration) preview = preview.replace(/📅 기간:.*/, `📅 기간:${newDuration.replace("기간:", "")}`);
    preview = preview
      .replace(/■ 전기료\s*:[\s\d,]+원/, `■ 전기료     : ${formatNumber(elecFee)}원`)
      .replace(/■ 관리비\s*:[\s\d,]+원/, `■ 관리비     : ${formatNumber(mgmtFee)}원`)
      .replace(/■ 월세\s*:[\s\d,]+원/,   `■ 월세       : ${formatNumber(rentFee)}원`)
      .replace(/💰 합계\s*:.*원/,  `💰 합계      : ${formatNumber(Math.abs(newTotal))}원`);
    const PRORATE_NOTICE = "정산주기는 전월 22일부터 당월 21일까지이며 입주달과 퇴거달에는 일할 적용 됩니다.";
    // 이체일 원본값 — 이미 안내문이 붙어있을 수 있으므로 제거 후 사용
    const baseIcheIl = (item.이체일 || "").replace(/\s*정산주기는.*$/, "").trim();
    // 퇴거일 있으면 이체일 표시 없음
    const newIcheIl  = newTotal < 0
      ? "환급금을 확인해주세요. 확인후 사전에 지정된 계좌로 입금됩니다. 이용해 주셔서 감사드립니다 🙇‍♂️ 🙇‍♀️"
      : baseIcheIl + (hasProrate ? " " + PRORATE_NOTICE : "");
    const etcLine = extrasTotal !== 0
      ? `■ 기타항목합 : ${formatNumber(extrasTotal)}원\n${extrasDetail}`
      : `■ 기타내역   : 해당사항 없음`;
    
   
    preview = preview
      .replace(/(■ 기타내역\s*:[^\n]*|■ 기타항목합\s*:[^\n]*(\n■\s[^\n]*)*)/g, etcLine)
      .replace(/■ 이체일[^\n]*/,                     newIcheIl);

 
    // 서버 캐시 업데이트용 최신 회원정보
    const cleanHosu  = String(editHosu).replace("호", "");
    const latestRoom = rooms.find((r) => String(r.room_no).replace("호", "") === cleanHosu);

    // 수신처/성함 - 음수(환급/퇴거) 여부에 따라 한 번만 계산
    const newSusinche = newTotal < 0
      ? `${editHosu} ${(latestRoom?.user_name || item.owner)}님 퇴실 정산서`
      : item.수신처.replace(/^.+?님/, `${(latestRoom?.user_name || item.owner)}님`);
    const newSungham = `${(latestRoom?.user_name || item.owner)}님`;

    saveAdjustMap({
      ...adjustMap,
      [editHosu]: {
        // dateChanged일 때만 날짜 저장 — 기본값(cycle_start/end)은 저장 안 함
        moveIn:     hasProrate ? editMoveIn  : "",
        moveOut:    hasProrate ? editMoveOut : "",
        moveInElec:  hasProrate ? editMoveInElec  : null,
        moveOutElec: hasProrate ? editMoveOutElec : null,
        extras: editExtras.filter((e) => e.name && e.amount),
        extrasDetail, extrasTotal, elecFee, mgmtFee, rentFee, total: newTotal, preview,
        사용기간: newDuration,
        이체일: newIcheIl,
        수신처: newSusinche,
        성함: newSungham,
      },
    });

    fetch(`${API_BASE}/api/update-billing-item`, {
      method: "POST", headers: authHeaders(),
      body: JSON.stringify({
        호수:       editHosu,
        수신처:     newSusinche,
        성함:       newSungham,
        전기료:     formatNumber(elecFee)   + "원",
        관리비:     formatNumber(mgmtFee)   + "원",
        월세:       formatNumber(rentFee)   + "원",
        기타항목합: extrasTotal !== 0 ? formatNumber(extrasTotal) + "원" : "0원",
        기타내역:   extrasDetail || "해당사항 없음",
        이체일:     newIcheIl,
        합계:       formatNumber(Math.abs(newTotal)),
        총금액:     formatNumber(Math.abs(newTotal)),
        미리보기:   preview,
        사용기간:   newDuration,
        // 최신 회원정보 반영
        ...(latestRoom ? (() => {
          const latestPhone = String(latestRoom.phone || "").replace(/-/g, "");
          const latestName  = latestRoom.user_name || "";
          const updatedPreview = preview
            .replace(/👤 성함: .+?님/, `👤 성함: ${latestName}님`);
          return {
            phone:        latestPhone,
            owner:        latestName,
            미리보기:     updatedPreview,
            email:        latestRoom.email        || "",
            deposit:      latestRoom.deposit      ?? 0,
            monthly_rent: latestRoom.monthly_rent ?? 0,
            mgmt_fee:     latestRoom.mgmt_fee     ?? 0,
            move_in_date: latestRoom.move_in_date ? latestRoom.move_in_date.slice(0, 10) : "",
            expiry_date:  latestRoom.expiry_date  ? latestRoom.expiry_date.slice(0, 10)  : "",
            memo:         latestRoom.memo         || "",
          };
        })() : {}),
      }),
    }).catch(() => {});

    setEditOpen(false);
  };

  const saveAdjustMap = (newMap) => {
    try { localStorage.setItem(ADJUST_KEY, JSON.stringify(newMap)); } catch {}
    setAdjustMap(newMap);
  };

  const clearEdit = (hosu) => {
    const n = { ...adjustMap }; delete n[hosu]; saveAdjustMap(n);
    // 서버 캐시에서도 해당 호수 _adjusted 제거 후 DB 원본으로 복구
    fetch(`${API_BASE}/api/reset-billing-item`, {
      method: "POST", headers: authHeaders(),
      body: JSON.stringify({ 호수: hosu }),
    }).then(() => fetchBillingData(true)).catch(() => {});
  };

  const clearAllAdjust = async () => {
    try { localStorage.removeItem(ADJUST_KEY); } catch {}
    setAdjustMap({});

    // 서버 캐시도 함께 초기화
    try {
      const res  = await fetch(`${API_BASE}/api/reset-billing-adjustments`, {
        method: "POST", headers: authHeaders(),
      });
      const data = await res.json();
      if (data.data && Array.isArray(data.data)) {
        setList(data.data);
        try { localStorage.setItem(CACHE_KEY, JSON.stringify(data.data)); } catch {}
      }
    } catch (e) {
      console.error("서버 초기화 실패:", e);
    }
  };
  const applyAdjust = (item) => {
    const adj = adjustMap[item.호수];
    if (!adj) return item;
    return {
      ...item,
      총금액:     String(Math.abs(adj.total)),
      전기료:     formatNumber(adj.elecFee)    + "원",
      관리비:     formatNumber(adj.mgmtFee)    + "원",
      월세:       formatNumber(adj.rentFee)    + "원",
      기타항목합: adj.extrasTotal !== 0 ? formatNumber(adj.extrasTotal) + "원" : "0원",
      기타내역:   adj.extrasDetail || "해당사항 없음",
      합계:       String(Math.abs(adj.total)),
      미리보기:   adj.preview,
      사용기간:   adj.사용기간 || item.사용기간,
      이체일:     adj.이체일   || item.이체일,
      수신처:     adj.수신처   || item.수신처,
      성함:       adj.성함     || item.성함,
      _adjusted:  true,
    };
  };

  // ── 알림톡/텔레그램 발송 ────────────────────────────────────

  const sendSingleTalk = async (item) => {
    const apiKey = window.prompt(`${item.호수} 알림톡 발송\n발송 암호를 입력하세요:`);
    if (apiKey === null) return;
    try {
      let res;
      if (item._adjusted) {
        res = await fetch(`${API_BASE}/api/send-kakao-custom?api_key=${apiKey}`, {
          method: "POST", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            phone: item.phone, 수신처: item.수신처, 성함: item.성함,
            호수_알림: item.호수_알림, 월: item.월, 사용기간: item.사용기간,
            전기료: item.전기료, 관리비: item.관리비, 월세: item.월세,
            기타항목합: item.기타항목합, 합계: formatNumber(Math.abs(Number(String(item.합계).replace(/[^0-9-]/g,''))||0)),
            기타내역: item.기타내역, 검침정보: item.검침정보, 이체일: item.이체일,
          }),
        });
      } else {
        res = await fetch(`${API_BASE}/api/send-kakao/${item.id}?api_key=${apiKey}`, { method: "POST", headers: authHeaders() });
      }
      if (res.status === 403) { alert("❌ 암호가 틀렸습니다."); return; }
      alert(res.ok ? "✅ 발송 요청 완료" : "❌ 발송 실패");
    } catch { alert("❌ 에러 발생"); }
  };

  const sendAllTalk = async () => {
    if (isSending) return;
    const apiKey = window.prompt(`전체 알림톡 발송 (${regularItems.length}세대)\n발송 암호를 입력하세요:`);
    if (apiKey === null) return;
    if (!window.confirm(`총 ${regularItems.length}세대에게 알림톡을 발송할까요?`)) return;
    setIsSending(true);
    try {
      const res    = await fetch(`${API_BASE}/api/send-kakao-all?api_key=${apiKey}`, { method: "POST", headers: authHeaders() });
      if (res.status === 403) { alert("❌ 암호가 틀렸습니다."); return; }
      const result = await res.json();
      alert(`✅ 발송 완료: ${result.success_count}세대\n${result.fail_count > 0 ? "❌ 실패: " + result.fail_list.join(", ") : ""}`);
    } catch { alert("❌ 에러 발생"); }
    finally  { setIsSending(false); }
  };

  const sendAllTelegram = async () => {
    if (isSending) return;
    if (!window.confirm(`총 ${regularItems.length}세대에게 텔레그램을 발송할까요?\n(미리보기 + XLS 파일)`)) return;
    setIsSending(true);
    try {
      const res  = await fetch(`${API_BASE}/api/send-telegram-all`, { method: "POST", headers: authHeaders() });
      const data = await res.json();
      if (res.ok) alert(`✅ 텔레그램 발송 완료: ${data.count}세대 미리보기 + XLS 파일`);
      else        alert("❌ 발송 실패");
    } catch { alert("❌ 에러 발생"); }
    finally  { setIsSending(false); }
  };

  const toggleMetric = (metric) =>
    setSelectedMetrics((prev) =>
      prev.includes(metric)
        ? (prev.length > 1 ? prev.filter((m) => m !== metric) : prev)
        : [...prev, metric]
    );

  // ══════════════════════════════════════════════════════════
  // 렌더
  // ══════════════════════════════════════════════════════════

  return (
    <div style={styles.container}>

      {/* 고지서 리스트 */}
      <div style={{ marginBottom: "20px" }}>
        <div style={{ display: "flex", gap: "6px", marginBottom: "10px" }}>
          <button onClick={() => { setIsTotalListOpen(!isTotalListOpen); setOwnerFilter(null); }}
            style={{ ...styles.btnFull("#007bff"), borderRadius: "12px", flex: 1 }}>
            <span>📋 고지서 현황 ({regularItems.length}세대)</span>
          </button>
          <button onClick={async () => { if (window.confirm("수정된 내용을 모두 초기화할까요?")) await clearAllAdjust(); }}
            style={{ padding: "0 12px", backgroundColor: "#ff6b6b", color: "#fff", border: "none", borderRadius: "12px", fontWeight: "bold", fontSize: "12px", cursor: "pointer", whiteSpace: "nowrap" }}>
            ↺ 초기화
          </button>
        </div>
        {summaryItems.length > 0
          ? summaryItems.map((item) => <SummaryCard key={item.id} item={item} regularCount={regularItems.length} regularItems={regularItems.map(applyAdjust)} ownerFilter={ownerFilter} setOwnerFilter={setOwnerFilter} setIsTotalListOpen={setIsTotalListOpen} />)
          : <div style={{ textAlign: "center", padding: "10px", color: "#888", fontSize: "12px" }}>데이터를 불러오는 중...</div>
        }
        {isTotalListOpen && (
          <div style={{ marginTop: "10px" }}>
            {(ownerFilter
              ? regularItems.filter((x) => {
                  const r = String(x.호수 || "").replace("호", "");
                  const g = OWNER_GROUPS.find(g => g.label === ownerFilter);
                  const all = OWNER_GROUPS.flatMap(g => g.rooms || []);
                  return g ? (g.rooms ? g.rooms.includes(r) : !all.includes(r)) : true;
                })
              : regularItems
            ).map((item) => (
              <RegularCard key={item.id} item={applyAdjust(item)} openId={openId} setOpenId={setOpenId}
                onSend={sendSingleTalk} onClearEdit={clearEdit} />
            ))}
          </div>
        )}
      </div>

      {/* 검침값 텔레그램 발송 */}
      <div style={styles.meterBox}>
        <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
          <input type="date" value={dailyDate} onChange={(e) => setDailyDate(e.target.value)}
            style={{ flex: 1.5, padding: "10px", borderRadius: "12px", border: "1px solid #ccc", fontSize: "12px", boxSizing: "border-box" }} />
          <div style={{ display: "flex", gap: "6px", alignItems: "center" }}>
            {[["elec", "전기"], ["gas", "가스"], ["water", "수도"]].map(([key, label]) => (
              <label key={key} style={{ fontSize: "11px", fontWeight: "bold", color: COLORS[key], cursor: "pointer", display: "flex", alignItems: "center", gap: "2px", whiteSpace: "nowrap" }}>
                <input type="checkbox" checked={selectedMetrics.includes(key)} onChange={() => toggleMetric(key)} style={{ accentColor: COLORS[key], width: "13px", height: "13px" }} />
                {label}
              </label>
            ))}
          </div>
          <button disabled={isMeterSending}
            onClick={async () => {
              if (isMeterSending) return;
              if (!dailyDate)              { alert("날짜를 선택해주세요"); return; }
              if (!selectedMetrics.length) { alert("항목을 하나 이상 선택해주세요"); return; }
              setIsMeterSending(true);
              try {
                const res  = await fetch(`${API_BASE}/api/send-meter-telegram?date=${dailyDate}&metrics=${selectedMetrics.join(",")}&tenant_id=${tenantId}`, { method: "POST" });
                const data = await res.json();
                alert(data.success ? `✅ ${data.message}` : `❌ ${data.message}`);
              } catch { alert("❌ 발송 실패"); }
              finally { setIsMeterSending(false); }
            }}
            style={{ ...styles.btnPrimary(isMeterSending ? "#9ca3af" : "#343a40"), fontSize: "11px", whiteSpace: "nowrap", flex: 1 }}>
            {isMeterSending ? "발송 중..." : "⚡ 검침값 발송"}
          </button>
        </div>
      </div>

      {/* ── 거주자 관리 바텀시트 ────────────────────────────── */}
      {residentOpen && (
        <div style={styles.overlay} onClick={() => setResidentOpen(false)}>
          <div style={styles.sheet} onClick={(e) => e.stopPropagation()}>

            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
              <div style={{ fontWeight: "bold", fontSize: "16px" }}>👥 거주자 관리</div>
              <button onClick={() => setResidentOpen(false)}
                style={{ background: "none", border: "none", fontSize: "18px", cursor: "pointer", color: "#9ca3af" }}>✕</button>
            </div>

            {/* 탭 */}
            <div style={{ display: "flex", gap: "6px", marginBottom: "16px" }}>
              {[["select", "📋 조회 / 수정"], ["add", L.add_btn]].map(([mode, label]) => (
                <button key={mode} onClick={() => { setResMode(mode); setSelRoomId(""); setResForm({}); setResMsg(""); }}
                  style={{ flex: 1, padding: "9px", borderRadius: "10px", border: "none", cursor: "pointer",
                           backgroundColor: resMode === mode ? "#10b981" : "#f1f5f9",
                           color:           resMode === mode ? "#fff"    : "#64748b",
                           fontWeight: "bold", fontSize: "13px" }}>
                  {label}
                </button>
              ))}
            </div>

            {/* 메시지 */}
            {resMsg && (
              <div style={{ padding: "10px", borderRadius: "8px", marginBottom: "12px", fontSize: "13px", fontWeight: "bold",
                            backgroundColor: resMsg.startsWith("✅") ? "#f0fdf4" : "#fef2f2",
                            color:           resMsg.startsWith("✅") ? "#15803d" : "#dc2626" }}>
                {resMsg}
              </div>
            )}

            {/* 조회 모드: 호수 선택 */}
            {resMode !== "add" && (
              <>
                <label style={styles.label}>{L.room_no} 선택</label>
                <select value={selRoomId} onChange={(e) => onSelectRoom(e.target.value)}
                  style={{ ...styles.input, backgroundColor: "#fff" }}>
                  <option value="">{L.select_ph}</option>
                  {rooms.map((r) => (
                    <option key={r.id} value={r.id}>{r.room_no}{L.room_suffix}</option>
                  ))}
                </select>
              </>
            )}

            {/* 폼 (수정 또는 신규) */}
            {(resMode === "edit" || resMode === "add") && (
              <>
                <ResidentForm form={resForm} setForm={setResForm} mode={resMode} L={L} />

                <div style={{ display: "flex", gap: "8px", marginTop: "4px", flexWrap: "wrap" }}>
                  {resMode === "edit" && (() => {
                    const room = rooms.find((r) => String(r.id) === String(selRoomId));
                    return room?.is_active ? (
                      <button onClick={deactivateResident} disabled={resSaving}
                        style={{ flex: 1, padding: "13px", borderRadius: "12px", border: "none", cursor: "pointer", backgroundColor: "#fff7ed", color: "#ea580c", fontWeight: "bold", fontSize: "13px" }}>
                        🚪 퇴거
                      </button>
                    ) : (
                      <button onClick={activateResident} disabled={resSaving}
                        style={{ flex: 1, padding: "13px", borderRadius: "12px", border: "none", cursor: "pointer", backgroundColor: "#f0fdf4", color: "#16a34a", fontWeight: "bold", fontSize: "13px" }}>
                        ✅ 활성
                      </button>
                    );
                  })()}
                  {resMode === "edit" && (
                    <button onClick={deleteResident} disabled={resSaving}
                      style={{ flex: 1, padding: "13px", borderRadius: "12px", border: "none", cursor: "pointer", backgroundColor: "#fef2f2", color: "#dc2626", fontWeight: "bold", fontSize: "13px" }}>
                      🗑 삭제
                    </button>
                  )}
                  <button onClick={resMode === "add" ? saveNewResident : saveResident} disabled={resSaving}
                    style={{ flex: 2, padding: "13px", borderRadius: "12px", border: "none", cursor: "pointer",
                             backgroundColor: resSaving ? "#9ca3af" : "#10b981",
                             color: "#fff", fontWeight: "bold", fontSize: "14px" }}>
                    {resSaving ? "저장 중..." : (resMode === "add" ? "✅ 등록" : "✅ 저장")}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* ── 관리비 수정 바텀시트 ────────────────────────────── */}
      {editOpen && (
        <div style={styles.overlay} onClick={() => setEditOpen(false)}>
          <div style={styles.sheet} onClick={(e) => e.stopPropagation()}>

            <div style={{ fontWeight: "bold", fontSize: "15px", marginBottom: "12px" }}>✏️ 관리비 수정</div>

            <select value={editHosu} onChange={(e) => onSelectHosu(e.target.value)}
              style={{ width: "100%", padding: "10px", borderRadius: "10px", border: "1px solid #e2e8f0", fontSize: "14px", backgroundColor: "#fff", outline: "none", marginBottom: "14px" }}>
              <option value="">{L.room_no} 선택</option>
              {regularItems.map((x) => <option key={x.호수} value={x.호수}>{x.호수} {x.owner}</option>)}
            </select>

            {(() => {
              const item    = regularItems.find((x) => x.호수 === editHosu);
              const { proRate, usedDays } = (() => {
                if (!editMoveIn && !editMoveOut) return { proRate: null, usedDays: null };
                const now    = new Date();
                const cEnd   = item?.cycle_end   ? new Date(item.cycle_end)   : new Date(now.getFullYear(), now.getMonth(), 21);
                const cStart = item?.cycle_start ? new Date(item.cycle_start) : new Date(now.getFullYear(), now.getMonth() - 1, 22);
                const start  = editMoveIn  ? new Date(editMoveIn)  : cStart;
                const end    = editMoveOut ? new Date(editMoveOut) : cEnd;
                const used   = Math.round((end - start) / 86_400_000) + 1;
                // 2월 예외: 청구 사이클에 2월이 포함되면 해당 연도 2월 말일을 분모로
                const hasFeb = cStart.getMonth() === 1 || cEnd.getMonth() === 1
                  || (cStart.getMonth() < 1 && cEnd.getMonth() > 1);
                let total = 30;
                if (hasFeb) {
                  const febYear = cStart.getMonth() === 1 ? cStart.getFullYear()
                    : cEnd.getMonth() === 1 ? cEnd.getFullYear()
                    : cStart.getFullYear();
                  const isLeap = (febYear % 4 === 0 && febYear % 100 !== 0) || febYear % 400 === 0;
                  total = isLeap ? 29 : 28;
                }
                return { proRate: Math.min(Math.max(used / total, 0), 1), usedDays: used };
              })();
              return (
                <>
                  <div style={{ fontSize: "12px", color: "#64748b", marginBottom: "14px" }}>
                    전기 {item?.전기료} / 관리비 {item?.관리비} / 월세 {item?.월세}
                    {proRate !== null && (
                      <span style={{ marginLeft: "8px", color: "#10b981", fontWeight: "bold" }}>
                        → 일할율 {(proRate * 100).toFixed(1)}% ({usedDays}일)
                      </span>
                    )}
                  </div>

                  {/* 입주/퇴거일 일할계산 */}
                  <div style={{ backgroundColor: "#f8fafc", borderRadius: "12px", padding: "14px", marginBottom: "14px" }}>
                    <div style={{ fontSize: "12px", fontWeight: "bold", color: "#475569", marginBottom: "10px" }}>
                      📅 입주/퇴거일 일할계산 <span style={{ fontWeight: "normal", color: "#94a3b8" }}>(해당없으면 빈칸)</span>
                    </div>
                    {[
                      ["입주일", editMoveIn, setEditMoveIn, setEditMoveInElec, editMoveInElec],
                      ["퇴거일", editMoveOut, setEditMoveOut, setEditMoveOutElec, editMoveOutElec],
                    ].map(([label, val, setVal, setSetter, elec]) => (
                      <div key={label} style={{ display: "flex", gap: "8px", alignItems: "center", marginBottom: "8px" }}>
                        <span style={{ fontSize: "12px", color: "#64748b", width: "40px" }}>{label}</span>
                        <input type="date" value={val}
                          onChange={(e) => { setVal(e.target.value); setDateChanged(true); fetchElecOnDate(e.target.value, editHosu, setSetter); }}
                          style={{ flex: 1, padding: "8px", borderRadius: "8px", border: "1px solid #e2e8f0", fontSize: "13px", outline: "none" }} />
                        {elec !== null && <span style={{ fontSize: "11px", color: "#3b82f6" }}>⚡{elec}</span>}
                      </div>
                    ))}
                  </div>

                  {/* 기타항목 */}
                  <div style={{ backgroundColor: "#f8fafc", borderRadius: "12px", padding: "14px", marginBottom: "16px" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "10px" }}>
                      <div style={{ fontSize: "12px", fontWeight: "bold", color: "#475569" }}>➕ 기타항목 (미납/연체료 등)</div>
                      <button onClick={addExtra}
                        style={{ padding: "4px 10px", backgroundColor: "#6366f1", color: "#fff", border: "none", borderRadius: "6px", fontSize: "11px", fontWeight: "bold", cursor: "pointer" }}>+ 추가</button>
                    </div>
                    {editExtras.length === 0
                      ? <div style={{ fontSize: "12px", color: "#94a3b8", textAlign: "center" }}>항목 없음</div>
                      : editExtras.map((ex, i) => (
                        <div key={i} style={{ display: "flex", gap: "6px", marginBottom: "8px", alignItems: "center" }}>
                          <input value={ex.name} onChange={(e) => updateExtra(i, "name", e.target.value)} placeholder="항목명"
                            style={{ flex: 1.5, padding: "8px", borderRadius: "8px", border: "1px solid #e2e8f0", fontSize: "12px", outline: "none" }} />
                          <input value={ex.amount} onChange={(e) => updateExtra(i, "amount", e.target.value)} placeholder="금액 (-가능)"
                            style={{ flex: 1, padding: "8px", borderRadius: "8px", border: "1px solid #e2e8f0", fontSize: "12px", outline: "none", textAlign: "right" }} />
                          <span style={{ fontSize: "11px", color: "#64748b" }}>원</span>
                          <button onClick={() => removeExtra(i)}
                            style={{ padding: "6px 8px", backgroundColor: "#fee2e2", color: "#ef4444", border: "none", borderRadius: "6px", cursor: "pointer", fontWeight: "bold" }}>✕</button>
                        </div>
                      ))
                    }
                  </div>

                  <div style={{ display: "flex", gap: "8px" }}>
                    <button onClick={() => setEditOpen(false)}
                      style={{ flex: 1, padding: "13px", borderRadius: "12px", border: "none", backgroundColor: "#f1f5f9", fontWeight: "bold", fontSize: "14px", cursor: "pointer" }}>취소</button>
                    {editHosu && (
                      <button onClick={() => { clearEdit(editHosu); setEditOpen(false); }}
                        style={{ flex: 1, padding: "13px", borderRadius: "12px", border: "none", backgroundColor: "#e2e8f0", fontWeight: "bold", fontSize: "14px", cursor: "pointer" }}>↩ 초기화</button>
                    )}
                    <button onClick={saveEdit} disabled={!editHosu}
                      style={{ flex: 2, padding: "13px", borderRadius: "12px", border: "none",
                               backgroundColor: editHosu ? "#f59e0b" : "#e2e8f0",
                               color: editHosu ? "#fff" : "#9ca3af",
                               fontWeight: "bold", fontSize: "14px", cursor: editHosu ? "pointer" : "default" }}>
                      ✅ 수정 적용
                    </button>
                  </div>
                </>
              );
            })()}
          </div>
        </div>
      )}
    </div>
  );
}

export default BillingSystem;
